/**
 * B2B draft-order checkout client — mirrors mobile recurring-cart API flow.
 */
(function (global) {
  'use strict';

  const LOCATION_ID_KEY = 'amigo_company_location_id';
  const LOCATION_NAME_KEY = 'amigo_company_location_name';

  function normalizeApiBase(url) {
    return (url || '').replace(/\/+$/, '');
  }

  function parsePrice(value) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : Number.MAX_VALUE;
  }

  function deliveryTypeSortPriority(option) {
    const title = (option.title || '').toLowerCase();
    if (option.type === 'pickup') return 0;
    if (title.includes('ground')) return 1;
    if (title.includes('2nd day')) return 2;
    if (option.type === 'local_delivery') return 3;
    return 4;
  }

  function isAllowedCarrierShippingOption(title) {
    const normalized = (title || '').toLowerCase();
    if (normalized.includes('free shipping')) return false;
    if (!normalized.includes('ups')) return false;
    return normalized.includes('ground') || normalized.includes('2nd day');
  }

  function filterVisibleDeliveryOptions(options) {
    const filtered = (options || []).filter(function (option) {
      // Native Shopify pickup / local delivery: keep even when free.
      if (option.type === 'pickup' || option.type === 'local_delivery') return true;
      // Carrier shipping: never show $0 / Free Shipping masquerades.
      if (option.type === 'shipping') {
        if (parsePrice(option.price) <= 0) return false;
        return isAllowedCarrierShippingOption(option.title);
      }
      return false;
    });
    return filtered.sort(function (a, b) {
      const priceDiff = parsePrice(a.price) - parsePrice(b.price);
      if (priceDiff !== 0) return priceDiff;
      return deliveryTypeSortPriority(a) - deliveryTypeSortPriority(b);
    });
  }

  function cheapestDeliveryOption(options) {
    if (!options || !options.length) return null;
    return options.reduce(function (best, opt) {
      return parsePrice(opt.price) < parsePrice(best.price) ? opt : best;
    });
  }

  function formatAddress(location) {
    const parts = [
      location.address1,
      location.address2,
      [location.city, location.province, location.zip].filter(Boolean).join(', '),
      location.country,
    ].filter(function (p) {
      return p && String(p).trim();
    });
    return parts.join('\n');
  }

  function formatMoney(amount) {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '$0.00';
    return '$' + n.toFixed(2);
  }

  function formatMoneyFromCents(cents) {
    const n = parseFloat(cents);
    if (!Number.isFinite(n)) return '$0.00';
    return formatMoney(n / 100);
  }

  function preferredDeliveryOption(options) {
    if (!options || !options.length) return null;
    const local = options.find(function (o) {
      return o.type === 'local_delivery';
    });
    if (local) return local;
    const shipping = options.filter(function (o) {
      return o.type === 'shipping';
    });
    if (shipping.length) {
      return shipping.reduce(function (best, opt) {
        return parsePrice(opt.price) < parsePrice(best.price) ? opt : best;
      });
    }
    const pickup = options.find(function (o) {
      return o.type === 'pickup';
    });
    if (pickup) return pickup;
    return options[0];
  }

  function formatDeliveryOptionTitle(option) {
    if (!option) return 'Delivery';
    if (option.type === 'pickup') return 'Pick Up';
    return option.title || 'Delivery';
  }

  const AmigoB2bCheckout = {
    config: {
      enabled: false,
      customerId: '',
      email: '',
      shop: '',
      apiBase: '',
      orderReviewUrl: '/pages/order-review',
      devMode: false,
    },

    init: function (config) {
      this.config = Object.assign({}, this.config, config || {});
      if (this.config.customerId != null && this.config.customerId !== '') {
        this.config.customerId = String(this.config.customerId);
      }
      this.config.apiBase = normalizeApiBase(this.config.apiBase);
      this.startCartCheckoutUiWatcher();
    },

    isEnabled: function () {
      return Boolean(this.config.enabled && this.config.customerId && this.config.apiBase);
    },

    getSelectedLocationId: function () {
      try {
        return localStorage.getItem(LOCATION_ID_KEY) || '';
      } catch (e) {
        return '';
      }
    },

    getSelectedLocationName: function () {
      try {
        return localStorage.getItem(LOCATION_NAME_KEY) || '';
      } catch (e) {
        return '';
      }
    },

    setSelectedLocation: function (location) {
      if (!location) return;
      try {
        localStorage.setItem(LOCATION_ID_KEY, location.id || '');
        localStorage.setItem(LOCATION_NAME_KEY, location.name || '');
      } catch (e) {
        /* ignore */
      }
    },

    clearSelectedLocation: function () {
      try {
        localStorage.removeItem(LOCATION_ID_KEY);
        localStorage.removeItem(LOCATION_NAME_KEY);
      } catch (e) {
        /* ignore */
      }
    },

    fetchCartLines: function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('Could not load cart');
          return res.json();
        })
        .then(function (cart) {
          return (cart.items || []).map(function (item) {
            return {
              variantId: String(item.variant_id),
              quantity: item.quantity,
              title: item.product_title,
              variantTitle: item.variant_title,
              linePrice: item.final_line_price,
            };
          });
        });
    },

    resolveCustomerIdentity: function () {
      const self = this;
      if (self.config.customerId) {
        return Promise.resolve(String(self.config.customerId));
      }

      if (
        typeof window !== 'undefined' &&
        window.meta &&
        window.meta.page &&
        window.meta.page.customerId
      ) {
        self.config.customerId = String(window.meta.page.customerId);
        return Promise.resolve(self.config.customerId);
      }

      return fetch('/apps/recurring-cart/storefront-customer', { credentials: 'same-origin' })
        .then(function (res) {
          return res.json();
        })
        .then(function (body) {
          if (body && body.ok && body.customerId) {
            self.config.customerId = String(body.customerId);
            if (body.email) {
              self.config.email = body.email;
            }
            console.info('[AmigoB2bCheckout] Resolved customer via app proxy:', self.config.customerId);
          } else {
            console.warn(
              '[AmigoB2bCheckout] No storefront customer session. Log in at the store, then refresh. ' +
                '(New customer accounts may not populate Liquid customer.)'
            );
          }
          return self.config.customerId || '';
        })
        .catch(function (err) {
          console.warn('[AmigoB2bCheckout] storefront-customer lookup failed:', err);
          return '';
        });
    },

    fetchCompanyLocations: function () {
      const cfg = this.config;
      const url =
        cfg.apiBase +
        '/api/customer-company-locations?customerId=' +
        encodeURIComponent(cfg.customerId) +
        '&shop=' +
        encodeURIComponent(cfg.shop);
      return fetch(url, { credentials: 'omit' })
        .then(function (res) {
          return res.json();
        })
        .then(function (body) {
          if (!body.ok) {
            console.error('[AmigoB2bCheckout] company-locations API error:', body.error, {
              shop: cfg.shop,
              customerId: cfg.customerId,
            });
            throw new Error(body.error || 'Failed to load locations');
          }
          return {
            hasCompanyAssignment: Boolean(body.hasCompanyAssignment),
            locations: body.locations || [],
          };
        });
    },

    removeRetailCheckoutButtons: function () {
      document
        .querySelectorAll(
          '[data-amigo-retail-checkout], .cart-drawer .cart__ctas button[name="checkout"], .cart__footer .cart__ctas button[name="checkout"], #CartDrawer-Checkout, #checkout[name="checkout"]'
        )
        .forEach(function (el) {
          el.remove();
        });
    },

    isReviewOrderVisible: function () {
      const drawerReview = document.querySelector('[data-amigo-review-order-drawer]');
      const cartReview = document.querySelector('[data-amigo-review-order]');
      return Boolean(
        (drawerReview && !drawerReview.hidden) || (cartReview && !cartReview.hidden)
      );
    },

    syncCartCheckoutUi: function () {
      if (this._probePending) {
        document.documentElement.classList.remove('amigo-b2b-enabled');
        return;
      }

      const eligible = Boolean(this.config.enabled);
      const reviewVisible = this.isReviewOrderVisible();

      if (eligible || reviewVisible) {
        document.documentElement.classList.add('amigo-b2b-enabled');
        if (eligible) {
          document.querySelectorAll('[data-amigo-b2b-ui]').forEach(function (el) {
            el.hidden = false;
          });
        }
        this.removeRetailCheckoutButtons();
        return;
      }

      document.documentElement.classList.remove('amigo-b2b-enabled');
      document.querySelectorAll('[data-amigo-b2b-ui]').forEach(function (el) {
        el.hidden = true;
      });
      this.ensureRetailCheckoutButtons();
    },

    ensureRetailCheckoutButtons: function () {
      if (this._probePending || this.config.enabled || this.isReviewOrderVisible()) {
        return;
      }

      const label = this.config.checkoutLabel || 'Check out';

      const drawerCtas = document.querySelector('.cart-drawer .cart__ctas');
      if (drawerCtas && !drawerCtas.querySelector('[data-amigo-retail-checkout]')) {
        const drawerBtn = document.createElement('button');
        drawerBtn.type = 'submit';
        drawerBtn.id = 'CartDrawer-Checkout';
        drawerBtn.className = 'cart__checkout-button button';
        drawerBtn.name = 'checkout';
        drawerBtn.setAttribute('form', 'CartDrawer-Form');
        drawerBtn.setAttribute('data-amigo-retail-checkout', '');
        drawerBtn.textContent = label;
        drawerCtas.appendChild(drawerBtn);
      }

      const cartCtas = document.querySelector('#main-cart-footer .cart__ctas, .cart__footer .cart__ctas');
      if (cartCtas && !cartCtas.querySelector('[data-amigo-retail-checkout]')) {
        const cartBtn = document.createElement('button');
        cartBtn.type = 'submit';
        cartBtn.id = 'checkout';
        cartBtn.className = 'cart__checkout-button button';
        cartBtn.name = 'checkout';
        cartBtn.setAttribute('form', 'cart');
        cartBtn.setAttribute('data-amigo-retail-checkout', '');
        cartBtn.textContent = label;
        cartCtas.appendChild(cartBtn);
      }
    },

    applyEligibility: function (eligible) {
      this._probePending = false;
      this.config.enabled = Boolean(eligible);

      const b2bElements = document.querySelectorAll('[data-amigo-b2b-ui]');
      b2bElements.forEach(function (el) {
        el.hidden = !eligible;
      });

      if (eligible && b2bElements.length > 0) {
        document.documentElement.classList.add('amigo-b2b-enabled');
        this.removeRetailCheckoutButtons();
        window.wpd_prevent_checkout = true;
      } else if (!this._probePending) {
        document.documentElement.classList.remove('amigo-b2b-enabled');
        window.wpd_prevent_checkout = false;
      }

      this.syncCartCheckoutUi();

      if (eligible && typeof document !== 'undefined' && document.body) {
        var onCart =
          document.body.classList.contains('template-cart') ||
          window.location.pathname.indexOf('/cart') !== -1;
        if (onCart) {
          window.wpd_prevent_checkout = true;
        }
      }

      document.dispatchEvent(
        new CustomEvent('amigo:b2b-eligibility', {
          detail: { eligible: Boolean(eligible) },
        })
      );
    },

    startCartCheckoutUiWatcher: function () {
      if (this._cartCheckoutUiWatcherStarted) return;
      this._cartCheckoutUiWatcherStarted = true;
      const self = this;
      const run = function () {
        self.syncCartCheckoutUi();
      };
      document.addEventListener('amigo:b2b-eligibility', run);
      document.addEventListener('DOMContentLoaded', run);
      setInterval(run, 400);
      if (typeof MutationObserver !== 'undefined') {
        new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
      }
    },

    probeAndEnable: function () {
      const self = this;
      if (this._probePromise) {
        return this._probePromise;
      }

      if (!this.config.customerId) {
        this._probePending = false;
        this.applyEligibility(false);
        return Promise.resolve({ eligible: false, locations: [] });
      }

      if (this.config.devMode) {
        console.warn(
          '[AmigoB2bCheckout] Dev mode enabled — showing B2B UI without API probe. Checkout on order review will not work until the backend is connected to this store.'
        );
        this._probePending = false;
        this.applyEligibility(true);
        return Promise.resolve({ eligible: true, locations: [] });
      }

      if (!this.config.apiBase) {
        this._probePending = false;
        this.applyEligibility(false);
        return Promise.resolve({ eligible: false, locations: [] });
      }

      this._probePending = true;
      this.syncCartCheckoutUi();

      this._probePromise = this.fetchCompanyLocations()
        .then(function (result) {
          self.applyEligibility(result.hasCompanyAssignment);
          console.info('[AmigoB2bCheckout] Company probe result:', {
            shop: self.config.shop,
            customerId: self.config.customerId,
            hasCompanyAssignment: result.hasCompanyAssignment,
            locationCount: result.locations.length,
          });
          return {
            eligible: result.hasCompanyAssignment,
            locations: result.locations,
          };
        })
        .catch(function (err) {
          console.error('[AmigoB2bCheckout] Company probe failed:', {
            shop: self.config.shop,
            customerId: self.config.customerId,
            message: err && err.message ? err.message : String(err),
          });
          self.applyEligibility(false);
          return { eligible: false, locations: [] };
        })
        .finally(function () {
          self._probePromise = null;
        });

      return this._probePromise;
    },

    fetchDeliveryOptions: function (lines, companyLocationId) {
      const cfg = this.config;
      return fetch(cfg.apiBase + '/api/mobile-draft-delivery-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: cfg.customerId,
          email: cfg.email,
          shop: cfg.shop,
          lines: lines.map(function (l) {
            return { variantId: l.variantId, quantity: l.quantity };
          }),
          companyLocationId: companyLocationId,
        }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) {
              throw new Error((body && body.error) || 'Failed to load delivery options');
            }
            return body;
          });
        })
        .then(function (body) {
          if (!body.ok) throw new Error(body.error || 'Failed to load delivery options');
          const visible = filterVisibleDeliveryOptions(body.options || []);
          return {
            sessionToken: body.sessionToken,
            options: visible,
            paymentDueInDays: body.paymentDueInDays,
            paymentTermsName: body.paymentTermsName,
            shippingAddress: body.shippingAddress,
          };
        });
    },

    fetchPricingPreview: function (params) {
      const cfg = this.config;
      return fetch(cfg.apiBase + '/api/mobile-draft-pricing-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: cfg.customerId,
          email: cfg.email,
          shop: cfg.shop,
          lines: params.lines.map(function (l) {
            return { variantId: l.variantId, quantity: l.quantity };
          }),
          companyLocationId: params.companyLocationId,
          shippingRateHandle: params.shippingRateHandle,
          sessionToken: params.sessionToken,
          paymentTermsChoice: params.paymentTermsChoice,
          note: params.note || '',
        }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (body) {
          if (!body.ok) throw new Error(body.error || 'Failed to load pricing');
          return body;
        });
    },

    createCheckout: function (params) {
      const cfg = this.config;
      return fetch(cfg.apiBase + '/api/mobile-cart-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: cfg.customerId,
          email: cfg.email,
          shop: cfg.shop,
          lines: params.lines.map(function (l) {
            return { variantId: l.variantId, quantity: l.quantity };
          }),
          note: params.note || '',
          companyLocationId: params.companyLocationId,
          shippingRateHandle: params.shippingRateHandle,
          sessionToken: params.sessionToken,
          paymentTermsChoice: params.paymentTermsChoice,
          presentation: 'browser',
        }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (body) {
          if (!body.ok) throw new Error(body.error || 'Checkout failed');
          return body;
        });
    },

    renderLocationPicker: function (container, options) {
      const self = this;
      const opts = options || {};
      const listEl = container.querySelector('[role="radiogroup"]') || container.querySelector('.b2b-location-picker__list');
      const loadingEl = container.querySelector('[data-loading]');
      const errorEl = container.querySelector('[data-error]');
      if (!listEl) return Promise.reject(new Error('Location picker container invalid'));

      if (container._amigoPickerRender) {
        return container._amigoPickerRender;
      }

      if (loadingEl) loadingEl.hidden = false;
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      listEl.innerHTML = '';

      container._amigoPickerRender = this.fetchCompanyLocations()
        .then(function (result) {
          const locations = result.locations || [];
          if (loadingEl) loadingEl.hidden = true;
          if (!locations.length) {
            if (errorEl) {
              errorEl.textContent = 'No company locations found for your account.';
              errorEl.hidden = false;
            }
            return locations;
          }

          listEl.innerHTML = '';

          const selectedId = self.getSelectedLocationId();
          let hasValidSelection = locations.some(function (loc) {
            return loc.id === selectedId;
          });

          if (locations.length === 1) {
            self.setSelectedLocation(locations[0]);
            hasValidSelection = true;
          } else if (!hasValidSelection) {
            self.clearSelectedLocation();
          }

          locations.forEach(function (location) {
            const id = 'amigo-loc-' + location.id.replace(/[^a-zA-Z0-9]/g, '');
            const label = document.createElement('label');
            label.className = 'b2b-location-picker__option';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = container.dataset.pickerId || 'amigo-location';
            input.value = location.id;
            input.id = id;
            input.className = 'b2b-location-picker__radio';
            if (location.id === self.getSelectedLocationId()) {
              input.checked = true;
            }
            const textWrap = document.createElement('div');
            textWrap.className = 'b2b-location-picker__option-text';
            const nameEl = document.createElement('strong');
            nameEl.textContent = location.name || location.companyName || 'Location';
            const addrEl = document.createElement('span');
            addrEl.className = 'b2b-location-picker__address';
            addrEl.textContent = formatAddress(location).replace(/\n/g, ', ');
            textWrap.appendChild(nameEl);
            textWrap.appendChild(addrEl);
            label.appendChild(input);
            label.appendChild(textWrap);
            listEl.appendChild(label);

            input.addEventListener('change', function () {
              if (!input.checked) return;
              self.setSelectedLocation(location);
              if (typeof opts.onChange === 'function') {
                opts.onChange(location);
              }
            });
          });

          if (typeof opts.onReady === 'function') {
            opts.onReady(locations, hasValidSelection || locations.length === 1);
          }
          return locations;
        })
        .catch(function (err) {
          if (loadingEl) loadingEl.hidden = true;
          if (errorEl) {
            errorEl.textContent = err.message || 'Failed to load locations';
            errorEl.hidden = false;
          }
          throw err;
        })
        .finally(function () {
          container._amigoPickerRender = null;
        });

      return container._amigoPickerRender;
    },

    debounce: function (fn, ms) {
      let timer;
      return function () {
        const args = arguments;
        const ctx = this;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(ctx, args);
        }, ms);
      };
    },

    filterVisibleDeliveryOptions: filterVisibleDeliveryOptions,
    cheapestDeliveryOption: cheapestDeliveryOption,
    preferredDeliveryOption: preferredDeliveryOption,
    formatDeliveryOptionTitle: formatDeliveryOptionTitle,
    formatMoney: formatMoney,
    formatMoneyFromCents: formatMoneyFromCents,
    formatAddress: formatAddress,
  };

  global.AmigoB2bCheckout = AmigoB2bCheckout;
})(typeof window !== 'undefined' ? window : globalThis);
